import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/layout/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, Building2, Users2, Bell, BellOff, Copy, MessageCircle, ExternalLink, Info, Sparkles, Send, Check, Mail, Calendar as CalendarIcon, RefreshCw, PlugZap, LogOut } from "lucide-react";
import { enablePushNotifications, disablePushNotifications, isPushSubscribed } from "@/lib/push";
import { GoogleLeadFormsCard } from "@/components/settings/GoogleLeadFormsCard";

export default function Settings({ initialTab = "team" }) {
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "company_admin";

  // Handle Google OAuth callback query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected") === "1") {
      toast.success("Google Calendar connected");
      window.history.replaceState({}, "", window.location.pathname + "?tab=calendar");
    } else if (params.get("google_error")) {
      toast.error(`Google connect failed: ${params.get("google_error")}`);
      window.history.replaceState({}, "", window.location.pathname + "?tab=calendar");
    }
  }, []);

  // Compute default tab from query string.
  // Also gracefully redirect deprecated tab names to their new home so old
  // deep-links (?tab=stages, ?tab=canned, ?tab=integrations, ?tab=google, ?tab=email,
  // ?tab=prefs, ?tab=mailbox, ?tab=ai, ?tab=depts, ?tab=branding) keep working.
  const LEGACY_TAB_MAP = {
    stages: "pipeline",
    canned: "playbook",
    integrations: "channels",
    google: "calendar",
    email: "email",
    prefs: "email",
    mailbox: "email",
    ai: "ai",
    depts: "team",
    branding: "branding",
  };
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get("tab");
  const resolvedTab = LEGACY_TAB_MAP[tabFromUrl] || tabFromUrl || initialTab;

  return (
    <div>
      <PageHeader overline="Config" title="Settings" subtitle="Team, pipeline & tasks, playbook, communications, AI, calendar and branding." />
      <div className="px-6 lg:px-10 pb-10">
        <Tabs defaultValue={resolvedTab}>
          <TabsList className="rounded-sm bg-secondary/40 flex-wrap h-auto">
            <TabsTrigger value="team" className="rounded-sm" data-testid="settings-tab-team">Team &amp; Roles</TabsTrigger>
            {isCompanyAdmin && <TabsTrigger value="pipeline" className="rounded-sm" data-testid="settings-tab-pipeline">Pipeline &amp; Tasks</TabsTrigger>}
            <TabsTrigger value="playbook" className="rounded-sm" data-testid="settings-tab-playbook">Playbook</TabsTrigger>
            <TabsTrigger value="billing" className="rounded-sm" data-testid="settings-tab-billing">Billing</TabsTrigger>
            <TabsTrigger value="channels" className="rounded-sm" data-testid="settings-tab-channels">Channels</TabsTrigger>
            <TabsTrigger value="email" className="rounded-sm" data-testid="settings-tab-email">Email</TabsTrigger>
            <TabsTrigger value="calendar" className="rounded-sm" data-testid="settings-tab-calendar">Calendar</TabsTrigger>
            {(isCompanyAdmin || user?.role === "division_manager") && <TabsTrigger value="ai" className="rounded-sm" data-testid="settings-tab-ai">AI &amp; Chatbot</TabsTrigger>}
            {isCompanyAdmin && <TabsTrigger value="branding" className="rounded-sm" data-testid="settings-tab-branding">Company Branding</TabsTrigger>}
          </TabsList>

          {/* Team & Roles — merged: Team + Departments */}
          <TabsContent value="team" className="mt-6 space-y-6"><TeamTab /><DepartmentsTab /></TabsContent>

          {/* Pipeline & Tasks — merged: Pipeline stages + Task statuses + Division schedule */}
          {isCompanyAdmin && (
            <TabsContent value="pipeline" className="mt-6 space-y-6">
              <StagesTab />
              <TaskStatusesCard />
              <DivisionScheduleCard />
            </TabsContent>
          )}

          {/* Playbook — canned responses with keywords + channels */}
          <TabsContent value="playbook" className="mt-6"><CannedTab /></TabsContent>

          {/* Billing Profile — company header + PayNow QR + bank details */}
          <TabsContent value="billing" className="mt-6 space-y-6">
            <BillingProfileTab />
            {isCompanyAdmin && <MonthlyStatementsCard />}
          </TabsContent>

          {/* Channels — WhatsApp, Meta (FB/IG). Admin-only. */}
          <TabsContent value="channels" className="mt-6"><ChannelsTab /></TabsContent>

          {/* Email — My Mailbox (per-user), SMTP config (admin), Email prefs */}
          <TabsContent value="email" className="mt-6 space-y-6">
            <MailboxTab />
            {isCompanyAdmin && <EmailTab />}
            <PrefsTab />
          </TabsContent>

          {/* Calendar — Google Calendar OAuth */}
          <TabsContent value="calendar" className="mt-6"><GoogleCalendarTab /></TabsContent>

          {/* AI & Chatbot — BYO-key LLM provider (admin + division manager) */}
          {(isCompanyAdmin || user?.role === "division_manager") && (
            <TabsContent value="ai" className="mt-6"><AiProviderTab /></TabsContent>
          )}

          {/* Company Branding — logo / colors */}
          {isCompanyAdmin && <TabsContent value="branding" className="mt-6"><BrandingTab /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}

function StagesTab() {
  const { data: stages = [] } = useQuery({ queryKey: ["stages"], queryFn: () => api.get("/pipeline/stages").then((r) => r.data) });
  const [name, setName] = useState("");
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: () => api.post("/pipeline/stages", { name, order: stages.length, is_won: false, is_lost: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stages"] }); setName(""); toast.success("Stage added"); },
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/pipeline/stages/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ["stages"] }) });
  return (
    <Card className="rounded-sm bg-card border-border p-6 max-w-2xl">
      <div className="font-display text-xl font-extrabold mb-4">Pipeline Stages</div>
      <div className="space-y-2 mb-4">
        {stages.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3 border border-border rounded-sm bg-secondary/20">
            <div className="flex-1">
              <div className="font-medium">{s.name}</div>
              <div className="flex gap-1 mt-1">
                {s.is_won && <Badge className="rounded-sm text-[10px] bg-green-500/20 text-green-400 border-green-500/30">Closed Won</Badge>}
                {s.is_lost && <Badge className="rounded-sm text-[10px] bg-red-500/20 text-red-400 border-red-500/30">Closed Lost</Badge>}
              </div>
            </div>
            {!s.is_won && !s.is_lost && <button onClick={() => del.mutate(s.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input className="rounded-sm bg-secondary/40" placeholder="New stage name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button className="rounded-sm bg-primary" onClick={() => add.mutate()} disabled={!name || add.isPending}><Plus className="h-4 w-4" /></Button>
      </div>
    </Card>
  );
}

function CannedTab() {
  const { data: items = [] } = useQuery({ queryKey: ["canned"], queryFn: () => api.get("/canned-responses").then((r) => r.data) });
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", keywords: "", channels: [] });
  const CHANNELS = [
    { key: "chat", label: "Web chat" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "Email" },
    { key: "facebook", label: "Facebook" },
    { key: "instagram", label: "Instagram" },
  ];
  const reset = () => { setEditingId(null); setForm({ title: "", body: "", keywords: "", channels: [] }); };

  const buildPayload = () => ({
    title: form.title,
    body: form.body,
    keywords: form.keywords.split(/[,\n]/).map((k) => k.trim()).filter(Boolean),
    channels: form.channels,
  });

  const add = useMutation({
    mutationFn: () => api.post("/canned-responses", buildPayload()),
    onSuccess: () => { reset(); qc.invalidateQueries({ queryKey: ["canned"] }); toast.success("Template added"); },
  });
  const upd = useMutation({
    mutationFn: () => api.patch(`/canned-responses/${editingId}`, buildPayload()),
    onSuccess: () => { reset(); qc.invalidateQueries({ queryKey: ["canned"] }); toast.success("Template updated"); },
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/canned-responses/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["canned"] }),
  });

  const toggleChannel = (k) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(k) ? f.channels.filter((x) => x !== k) : [...f.channels, k],
    }));
  };
  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      body: item.body || "",
      keywords: (item.keywords || []).join(", "),
      channels: item.channels || [],
    });
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="playbook-tab">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display text-xl font-extrabold">{editingId ? "Edit Template" : "Add Playbook Template"}</div>
          {editingId && <button onClick={reset} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-white" data-testid="playbook-cancel-edit">Cancel</button>}
        </div>
        <p className="text-xs text-muted-foreground mb-4">Templates surface as one-tap suggestions in the Inbox when an incoming message matches any of the keywords below.</p>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="playbook-title" className="rounded-sm mt-1.5 bg-secondary/40" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Pricing FAQ" /></div>
          <div><Label>Reply body</Label><Textarea data-testid="playbook-body" rows={5} className="rounded-sm mt-1.5 bg-secondary/40" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Hi there — our starter plan is $99/month…" /></div>
          <div>
            <Label>Trigger keywords <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— comma separated</span></Label>
            <Input data-testid="playbook-keywords" className="rounded-sm mt-1.5 bg-secondary/40" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="price, pricing, cost, quote" />
          </div>
          <div>
            <Label>Show for channels <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— leave empty for all</span></Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {CHANNELS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleChannel(c.key)}
                  className={`text-[11px] uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${form.channels.includes(c.key) ? "bg-primary/20 border-primary text-primary" : "bg-secondary/40 border-border text-muted-foreground hover:text-white"}`}
                  data-testid={`playbook-channel-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          {editingId ? (
            <Button data-testid="playbook-save-edit" className="rounded-sm bg-primary" onClick={() => upd.mutate()} disabled={!form.title || !form.body || upd.isPending}>Save changes</Button>
          ) : (
            <Button data-testid="playbook-add" className="rounded-sm bg-primary" onClick={() => add.mutate()} disabled={!form.title || !form.body || add.isPending}>Add template</Button>
          )}
        </div>
      </Card>
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="font-display text-xl font-extrabold mb-4">Existing templates ({items.length})</div>
        <div className="space-y-2" data-testid="playbook-list">
          {items.length === 0 && <div className="text-sm text-muted-foreground">No templates yet — add your first pricing / booking / thank-you reply on the left.</div>}
          {items.map((c) => (
            <div key={c.id} className="p-3 border border-border rounded-sm bg-secondary/20" data-testid={`playbook-item-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium text-sm">{c.title}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => startEdit(c)} className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary" data-testid={`playbook-edit-${c.id}`}>Edit</button>
                  <button onClick={() => del.mutate(c.id)} className="text-muted-foreground hover:text-red-400" data-testid={`playbook-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div className="text-xs text-muted-foreground whitespace-pre-wrap mb-2">{c.body}</div>
              {(c.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1">
                  {c.keywords.map((k) => (
                    <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary uppercase tracking-widest">{k}</span>
                  ))}
                </div>
              )}
              {(c.channels || []).length > 0 && (
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Channels: {c.channels.join(", ")}</div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function TeamTab() {
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const qc = useQueryClient();
  const invite = useMutation({
    mutationFn: () => api.post("/users", form),
    onSuccess: () => { setForm({ name: "", email: "", password: "", role: "agent" }); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User invited — welcome email sent"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="font-display text-xl font-extrabold mb-4">Invite Team Member</div>
        <div className="space-y-3">
          <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div><Label>Temporary Password</Label><Input type="text" className="rounded-sm mt-1.5 bg-secondary/40" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <div>
            <Label>Role</Label>
            <select className="w-full mt-1.5 rounded-sm bg-secondary/40 border border-border px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="agent">Agent / Salesperson</option>
              <option value="division_manager">Division Manager</option>
              <option value="company_admin">Company Admin</option>
            </select>
          </div>
          <Button className="rounded-sm bg-primary" onClick={() => invite.mutate()} disabled={!form.name || !form.email || form.password.length < 6}>Invite</Button>
        </div>
      </Card>
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="font-display text-xl font-extrabold mb-4">Team</div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-3 p-3 border border-border rounded-sm bg-secondary/20">
              <div className="h-9 w-9 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold">{u.name[0]}</div>
              <div className="flex-1">
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-muted-foreground">{u.email}</div>
              </div>
              <Badge variant="secondary" className="rounded-sm text-[10px] capitalize">{u.role?.replace("_", " ")}</Badge>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function EmailTab() {
  const { data: status, isLoading } = useQuery({ queryKey: ["email-status"], queryFn: () => api.get("/email/status").then((r) => r.data) });
  const [to, setTo] = useState("");
  const send = useMutation({
    mutationFn: () => api.post("/email/test", { to }),
    onSuccess: () => toast.success("Test email sent — check inbox"),
    onError: (e) => toast.error(e.response?.data?.detail || "Send failed"),
  });
  return (
    <Card className="rounded-sm bg-card border-border p-6 max-w-2xl">
      <div className="font-display text-xl font-extrabold mb-1">Email / SMTP</div>
      <div className="text-xs text-muted-foreground mb-6">Booking confirmations, team invites and new-lead notifications are sent via this server.</div>
      {isLoading || !status ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Field label="Status" value={status.configured ? "Connected" : "Not configured"} highlight={status.configured} />
            <Field label="Server" value={status.host || "—"} />
            <Field label="Port" value={status.port || "—"} />
            <Field label="From" value={status.from_email ? `${status.from_name} <${status.from_email}>` : "—"} />
          </div>
          <div>
            <Label>Send a test email</Label>
            <div className="flex gap-2 mt-1.5">
              <Input type="email" className="rounded-sm bg-secondary/40" placeholder="you@example.com" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to" />
              <Button data-testid="email-test-send" className="rounded-sm bg-primary" onClick={() => send.mutate()} disabled={!to.includes("@") || send.isPending}>Send Test</Button>
            </div>
          </div>
          <div className="mt-6 p-3 border border-border rounded-sm bg-secondary/20 text-xs text-muted-foreground leading-relaxed">
            <b className="text-white block mb-1">Where emails are sent from LeadifyCRM</b>
            · Public meeting bookings → confirmation to the visitor + notification to the agent<br />
            · Team invites → welcome email with temporary credentials<br />
            · Inbox conversation tagged as "Lead" → notification to the assigned agent
          </div>
        </>
      )}
    </Card>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div className="p-3 border border-border rounded-sm bg-secondary/20">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
      <div className={`text-sm font-medium mt-0.5 truncate ${highlight ? "text-green-400" : ""}`}>{value}</div>
    </div>
  );
}

function PrefsTab() {
  const { data, isLoading } = useQuery({ queryKey: ["prefs"], queryFn: () => api.get("/users/me/email-prefs").then((r) => r.data) });
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (payload) => api.patch("/users/me/email-prefs", payload),
    onSuccess: (r) => { qc.setQueryData(["prefs"], r.data); toast.success("Preferences saved"); },
  });
  if (isLoading || !data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const items = [
    { key: "new_lead", label: "New Lead Notifications", desc: "Get an email when a conversation you're assigned to is categorized as a Lead." },
    { key: "booking", label: "Booking Notifications", desc: "Get an email when someone books, reschedules or cancels a meeting with you." },
    { key: "task_alerts", label: "Daily Task Digest", desc: "One email every morning with your overdue and due-today project tasks." },
    { key: "weekly_digest", label: "Weekly Digest", desc: "Weekly summary of your pipeline performance (coming soon)." },
  ];
  return (
    <Card className="rounded-sm bg-card border-border p-6 max-w-2xl">
      <div className="font-display text-xl font-extrabold mb-1">Email Preferences</div>
      <div className="text-xs text-muted-foreground mb-6">Choose which emails you receive from LeadifyCRM.</div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.key} className="flex items-start gap-4 p-4 border border-border rounded-sm bg-secondary/20">
            <div className="flex-1">
              <div className="font-medium">{it.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{it.desc}</div>
            </div>
            <Switch checked={!!data[it.key]} onCheckedChange={(v) => mut.mutate({ [it.key]: v })} data-testid={`prefs-${it.key}`} />
          </div>
        ))}
      </div>
      <PushToggle />
    </Card>
  );
}

function PushToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [supported] = useState(() => "Notification" in window && "serviceWorker" in navigator && "PushManager" in window);

  useEffect(() => { isPushSubscribed().then(setEnabled); }, []);

  const toggle = async (v) => {
    setLoading(true);
    try {
      if (v) { await enablePushNotifications(); setEnabled(true); toast.success("Push notifications enabled"); }
      else { await disablePushNotifications(); setEnabled(false); toast.success("Push notifications disabled"); }
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Browser Push</div>
      <div className="flex items-start gap-4 p-4 border border-primary/30 rounded-sm bg-primary/5">
        <div className="flex-1">
          <div className="font-medium flex items-center gap-2">{enabled ? <Bell className="h-4 w-4 text-primary" /> : <BellOff className="h-4 w-4 text-muted-foreground" />} Desktop / Mobile Push Notifications</div>
          <div className="text-xs text-muted-foreground mt-0.5">Get alerts about new leads, transfers, and bookings even when this tab isn't open.</div>
          {!supported && <div className="text-xs text-red-400 mt-2">Your browser doesn't support push notifications.</div>}
        </div>
        <Switch checked={enabled} onCheckedChange={toggle} disabled={loading || !supported} data-testid="push-toggle" />
      </div>
    </div>
  );
}

function BrandingTab() {
  const { data: company } = useQuery({ queryKey: ["company"], queryFn: () => api.get("/company").then((r) => r.data) });
  const [name, setName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  React.useEffect(() => { if (company) { setName(company.name || ""); setLogoUrl(company.logo_url || ""); } }, [company]);
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.patch("/company", { name, logo_url: logoUrl }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["company"] }); toast.success("Branding saved — all emails will now use it"); },
  });
  return (
    <Card className="rounded-sm bg-card border-border p-6 max-w-2xl">
      <div className="font-display text-xl font-extrabold mb-1">Company Branding</div>
      <div className="text-xs text-muted-foreground mb-6">This shows in booking confirmations, team invites, lead notifications and password reset emails.</div>
      <div className="space-y-4">
        <div><Label>Company name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} data-testid="brand-name-input" /></div>
        <div><Label>Logo URL</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://your-cdn.com/logo.png" data-testid="brand-logo-input" /></div>
        {logoUrl && (
          <div className="p-4 border border-border rounded-sm bg-secondary/10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Preview</div>
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="Logo" className="h-8 w-auto rounded-sm border border-border" onError={(e) => e.target.style.display = 'none'} />
              <div className="font-display font-extrabold">{name}</div>
            </div>
          </div>
        )}
        <Button className="rounded-sm bg-primary" onClick={() => mut.mutate()} disabled={mut.isPending || !name} data-testid="brand-save-button">Save branding</Button>
      </div>
    </Card>
  );
}

function DepartmentsTab() {
  const { data: depts = [] } = useQuery({ queryKey: ["depts"], queryFn: () => api.get("/departments").then((r) => r.data) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [slaMinutes, setSlaMinutes] = useState(240);
  const [memberIds, setMemberIds] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const reset = () => { setName(""); setDesc(""); setMemberIds([]); setSlaMinutes(240); setEditingId(null); };

  const save = useMutation({
    mutationFn: () => {
      const payload = { name, description: desc, member_ids: memberIds, sla_minutes: Number(slaMinutes) };
      return editingId ? api.patch(`/departments/${editingId}`, payload) : api.post("/departments", payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["depts"] }); reset(); toast.success(editingId ? "Department updated" : "Department created"); },
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/departments/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["depts"] }); toast.success("Deleted"); },
  });

  const startEdit = (d) => { setEditingId(d.id); setName(d.name); setDesc(d.description || ""); setMemberIds(d.member_ids || []); setSlaMinutes(d.sla_minutes || 240); };
  const toggleMember = (id) => setMemberIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="font-display text-xl font-extrabold mb-1">{editingId ? "Edit Department" : "New Department"}</div>
        <div className="text-xs text-muted-foreground mb-4">Group agents by function (e.g. Sales, Support, Billing). Transfer inbox conversations to a whole department and enforce SLA.</div>
        <div className="space-y-3">
          <div><Label>Name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales" data-testid="dept-name-input" /></div>
          <div><Label>Description</Label><Textarea rows={2} className="rounded-sm mt-1.5 bg-secondary/40" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
          <div>
            <Label>Response SLA (minutes)</Label>
            <Input type="number" className="rounded-sm mt-1.5 bg-secondary/40" value={slaMinutes} onChange={(e) => setSlaMinutes(e.target.value)} placeholder="240" data-testid="dept-sla-input" />
            <div className="text-[10px] text-muted-foreground mt-1">Conversations routed to this department must be responded to within this window.</div>
          </div>
          <div>
            <Label>Members</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {users.map((u) => (
                <button key={u.id} onClick={() => toggleMember(u.id)} type="button" className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${memberIds.includes(u.id) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>{u.name}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button className="rounded-sm bg-primary" onClick={() => save.mutate()} disabled={!name || save.isPending} data-testid="dept-save">{editingId ? "Update" : "Create"}</Button>
            {editingId && <Button variant="outline" className="rounded-sm" onClick={reset}>Cancel</Button>}
          </div>
        </div>
      </Card>

      <Card className="rounded-sm bg-card border-border p-6">
        <div className="font-display text-xl font-extrabold mb-4">Existing Departments</div>
        <div className="space-y-2">
          {depts.length === 0 && <div className="text-sm text-muted-foreground">No departments yet.</div>}
          {depts.map((d) => (
            <div key={d.id} className="p-3 border border-border rounded-sm bg-secondary/20" data-testid="dept-row">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Users2 className="h-4 w-4 text-primary" />
                  <div className="font-medium">{d.name}</div>
                  <Badge variant="secondary" className="rounded-sm text-[10px]">{(d.members || []).length} members</Badge>
                  <Badge variant="outline" className="rounded-sm text-[10px]">SLA {d.sla_minutes || 240}m</Badge>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(d)} className="text-xs text-muted-foreground hover:text-primary">Edit</button>
                  <button onClick={() => del.mutate(d.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              {d.description && <div className="text-xs text-muted-foreground">{d.description}</div>}
              <div className="flex flex-wrap gap-1 mt-2">
                {(d.members || []).map((m) => <Badge key={m.id} variant="outline" className="rounded-sm text-[10px]">{m.name}</Badge>)}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}


function IntegrationsCopyField({ label, value, testId, onCopy }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input readOnly value={value || ""} data-testid={testId} className="rounded-sm bg-secondary/40 font-mono text-xs" />
        <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => onCopy(value || "")} data-testid={`${testId}-copy`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ChannelsTab() {
  const { data: wa } = useQuery({
    queryKey: ["wa-info"],
    queryFn: () => api.get("/integrations/whatsapp/info").then((r) => r.data),
  });

  const copy = (v) => {
    Promise.resolve().then(() => navigator.clipboard.writeText(v)).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed"));
  };

  const ext = wa?.chrome_extension || {};
  const cloud = wa?.cloud_api || {};

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Section: WhatsApp */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">WhatsApp</div>
      </div>
      {/* WhatsWizard (Chrome extension) */}
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-green-500/15 border border-green-500/30 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-green-400" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">WhatsApp via WhatsWizard</div>
              <div className="text-xs text-muted-foreground">Personal WhatsApp Web bridge (Chrome extension). Best for individual salespeople.</div>
            </div>
          </div>
          <Badge className="rounded-sm bg-green-500/10 text-green-400 border-green-500/30">Recommended</Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <IntegrationsCopyField label="Webhook URL (inbound)" value={ext.webhook_url} testId="wa-webhook-url" onCopy={copy} />
          <IntegrationsCopyField label="Polling URL (outbound sends)" value={ext.polling_url} testId="wa-polling-url" onCopy={copy} />
          <IntegrationsCopyField label="Delivery-status URL" value={ext.delivery_status_url} testId="wa-delivery-status-url" onCopy={copy} />
          <IntegrationsCopyField label="Dead-letter URL (permanent failures)" value={ext.dead_letter_url} testId="wa-dead-letter-url" onCopy={copy} />
          <IntegrationsCopyField label="API Key" value={ext.api_key} testId="wa-api-key" onCopy={copy} />
          <IntegrationsCopyField label="Webhook Secret (optional)" value={ext.webhook_secret} testId="wa-webhook-secret" onCopy={copy} />
        </div>

        <div className="mt-6 p-4 rounded-sm bg-secondary/40 border border-border">
          <div className="flex items-center gap-2 text-sm font-semibold mb-2">
            <Info className="h-4 w-4 text-primary" />
            Setup in WhatsWizard popup
          </div>
          <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal ml-5">
            {(ext.whatswizard_setup || []).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
          <div className="mt-3 pt-3 border-t border-border text-xs text-yellow-400/90">
            <strong>Heads up:</strong> WhatsApp Web renders messages you send to yourself as outgoing (<code>.message-out</code>). WhatsWizard only observes incoming messages by default, so self-tests won&apos;t appear in the inbox — test with a message from a <em>different</em> phone/account.
          </div>
        </div>

        <div className="mt-4 p-4 rounded-sm bg-secondary/30 border border-dashed border-border">
          <div className="text-sm font-semibold mb-2">How outbound replies work</div>
          <ol className="text-xs text-muted-foreground space-y-1 list-decimal ml-5">
            <li>Open a WhatsApp conversation and set the contact&apos;s WhatsApp phone (top-right pill in the header).</li>
            <li>Reply from the Inbox — LeadifyCRM queues the message into an outbox.</li>
            <li>WhatsWizard polls this endpoint every few seconds, opens the chat in WhatsApp Web, and types the reply with human-like delays.</li>
          </ol>
        </div>
      </Card>

      {/* Per-division WhatsWizard API keys */}
      <WhatsExtCredentialsCard copy={copy} />

      {/* WhatsApp Cloud API */}
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <MessageCircle className="h-4 w-4 text-blue-400" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">WhatsApp Cloud API (Meta)</div>
              <div className="text-xs text-muted-foreground">Official business API — requires Meta Business + verified number.</div>
            </div>
          </div>
          <Badge className="rounded-sm bg-blue-500/10 text-blue-400 border-blue-500/30" variant="outline">Advanced</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
          <IntegrationsCopyField label="Callback URL" value={cloud.callback_url} testId="wa-cloud-callback" onCopy={copy} />
          <IntegrationsCopyField label="Verify Token" value={cloud.verify_token} testId="wa-cloud-verify" onCopy={copy} />
        </div>
        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
          <ExternalLink className="h-3 w-3" />
          Configure in Meta developer console → your WhatsApp app → Configuration → paste these values.
        </div>
      </Card>

      {/* Section: Meta (FB/IG) */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Meta — Facebook &amp; Instagram</div>
      </div>
      <MetaIntegrationCard copy={copy} />

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Google Ads — Lead Forms</div>
      </div>
      <GoogleLeadFormsCard copy={copy} />
    </div>
  );
}



// ==============================================================
// AI Provider (per-division) — bring-your-own-key LLM configuration
// ==============================================================
function AiProviderTab() {
  const qc = useQueryClient();
  const { data: providersMeta } = useQuery({
    queryKey: ["llm-providers-list"],
    queryFn: () => api.get("/settings/llm-provider/providers").then((r) => r.data.providers),
  });
  const { data: current, isLoading } = useQuery({
    queryKey: ["llm-provider"],
    queryFn: () => api.get("/settings/llm-provider").then((r) => r.data),
  });

  const [form, setForm] = useState({ provider: "openai", model: "", api_key: "", base_url: "" });
  const [dirty, setDirty] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (current?.configured) {
      setForm({
        provider: current.provider || "openai",
        model: current.model || "",
        api_key: "",  // never populate the actual key
        base_url: current.base_url || "",
      });
    }
  }, [current]);

  const selected = (providersMeta || []).find((p) => p.key === form.provider);
  const needsBaseUrl = selected?.needs_base_url;

  const saveMut = useMutation({
    mutationFn: (body) => api.put("/settings/llm-provider", body).then((r) => r.data),
    onSuccess: (d) => { qc.setQueryData(["llm-provider"], d); setDirty(false); toast.success("AI provider saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const testMut = useMutation({
    mutationFn: () => api.post("/settings/llm-provider/test").then((r) => r.data),
    onSuccess: (d) => {
      if (d?.ok) { setTestResult({ ok: true, reply: d.reply }); toast.success("Provider responded"); }
      else { setTestResult({ ok: false, error: d?.error || "Test failed" }); toast.error("Provider test failed"); }
    },
    onError: (e) => setTestResult({ ok: false, error: e?.response?.data?.detail || e?.response?.data?.error || "Test failed" }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete("/settings/llm-provider"),
    onSuccess: () => { qc.setQueryData(["llm-provider"], { configured: false, provider: null }); setForm({ provider: "openai", model: "", api_key: "", base_url: "" }); toast.success("Provider cleared"); },
  });

  const submit = () => {
    if (needsBaseUrl && !form.base_url.trim()) return toast.error("Base URL is required for custom providers");
    if (!current?.configured && !form.api_key.trim()) return toast.error("API key is required on first save");
    saveMut.mutate({
      provider: form.provider,
      model: form.model || undefined,
      api_key: form.api_key || undefined,
      base_url: needsBaseUrl ? form.base_url : (form.base_url || undefined),
    });
  };

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="ai-provider-tab">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">AI Provider</div>
              <div className="text-xs text-muted-foreground">Powers the chatbot + auto-flag Lead classifier. Bring your own key.</div>
            </div>
          </div>
          {current?.configured && (
            <Badge className="rounded-sm bg-green-500/10 text-green-400 border-green-500/30" data-testid="llm-configured-badge">
              <Check className="h-3 w-3 mr-1" /> Configured
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Provider</Label>
            <Select value={form.provider} onValueChange={(v) => { setForm({ ...form, provider: v, model: "" }); setDirty(true); }}>
              <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="llm-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(providersMeta || []).map((p) => (
                  <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Model {selected?.default_model && <span className="text-muted-foreground text-xs ml-1">(default: {selected.default_model})</span>}</Label>
            <Input
              data-testid="llm-model-input"
              className="mt-1.5 rounded-sm bg-secondary/40"
              placeholder={selected?.default_model || "e.g. gpt-4o-mini"}
              value={form.model}
              onChange={(e) => { setForm({ ...form, model: e.target.value }); setDirty(true); }}
            />
          </div>

          <div className={needsBaseUrl ? "md:col-span-2" : ""}>
            <Label>API Key {current?.configured && <span className="text-muted-foreground text-xs ml-1">· leave blank to keep existing ({current.api_key_preview})</span>}</Label>
            <Input
              data-testid="llm-api-key-input"
              type="password"
              className="mt-1.5 rounded-sm bg-secondary/40 font-mono"
              placeholder={current?.configured ? "•••• keeping current key ••••" : "sk-..."}
              value={form.api_key}
              onChange={(e) => { setForm({ ...form, api_key: e.target.value }); setDirty(true); }}
            />
          </div>

          {needsBaseUrl && (
            <div className="md:col-span-2">
              <Label>Base URL <span className="text-red-400">*</span></Label>
              <Input
                data-testid="llm-base-url-input"
                className="mt-1.5 rounded-sm bg-secondary/40 font-mono"
                placeholder="http://your-local-model.example.com/v1"
                value={form.base_url}
                onChange={(e) => { setForm({ ...form, base_url: e.target.value }); setDirty(true); }}
              />
              <div className="text-xs text-muted-foreground mt-1">
                Must be reachable from the internet (Ollama, LM Studio, vLLM etc). Use HTTPS in production.
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <Button className="rounded-sm bg-primary" onClick={submit} disabled={saveMut.isPending} data-testid="llm-save-btn">
            {saveMut.isPending ? "Saving…" : "Save provider"}
          </Button>
          <Button variant="outline" className="rounded-sm" onClick={() => testMut.mutate()} disabled={!current?.configured || testMut.isPending || dirty} data-testid="llm-test-btn">
            <Send className="h-3.5 w-3.5 mr-1.5" />{testMut.isPending ? "Testing…" : "Send test prompt"}
          </Button>
          {current?.configured && (
            <Button variant="ghost" className="rounded-sm text-red-400 hover:text-red-300 hover:bg-red-500/10" onClick={() => { if (window.confirm("Remove the current LLM provider config?")) deleteMut.mutate(); }} data-testid="llm-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />Clear
            </Button>
          )}
        </div>

        {testResult && (
          <div className={`mt-4 p-3 rounded-sm border text-xs ${testResult.ok ? "border-green-500/30 bg-green-500/10 text-green-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`} data-testid="llm-test-result">
            {testResult.ok ? (<><span className="font-bold">Reply:</span> {testResult.reply}</>) : (<><span className="font-bold">Error:</span> {testResult.error}</>)}
          </div>
        )}
      </Card>

      <Card className="rounded-sm bg-secondary/30 border border-dashed border-border p-5">
        <div className="text-sm font-semibold flex items-center gap-2"><Info className="h-4 w-4 text-primary" />Where to get an API key</div>
        <ul className="mt-2 text-xs text-muted-foreground space-y-1.5 list-disc ml-5">
          <li><b>OpenAI</b> — <a href="https://platform.openai.com/api-keys" className="text-primary hover:underline" target="_blank" rel="noreferrer">platform.openai.com/api-keys</a></li>
          <li><b>Anthropic (Claude)</b> — <a href="https://console.anthropic.com/settings/keys" className="text-primary hover:underline" target="_blank" rel="noreferrer">console.anthropic.com/settings/keys</a></li>
          <li><b>Google Gemini</b> — <a href="https://aistudio.google.com/apikey" className="text-primary hover:underline" target="_blank" rel="noreferrer">aistudio.google.com/apikey</a></li>
          <li><b>DeepSeek</b> — <a href="https://platform.deepseek.com/api_keys" className="text-primary hover:underline" target="_blank" rel="noreferrer">platform.deepseek.com/api_keys</a></li>
          <li><b>Custom</b> — any OpenAI-compatible endpoint (Ollama, LM Studio, vLLM, Groq, Together, Fireworks). Paste base URL + key.</li>
        </ul>
      </Card>
    </div>
  );
}


// ==============================================================
// Personal IMAP Mailbox — each user connects their own inbox
// ==============================================================
function MailboxTab() {
  const qc = useQueryClient();
  const { data: mb, isLoading } = useQuery({
    queryKey: ["my-mailbox"],
    queryFn: () => api.get("/integrations/imap/mailbox").then((r) => r.data),
  });
  const { data: choices } = useQuery({
    queryKey: ["imap-intervals"],
    queryFn: () => api.get("/integrations/imap/interval-choices").then((r) => r.data),
  });

  const [form, setForm] = useState({
    email_address: "", imap_host: "", imap_port: 993, imap_use_ssl: true,
    imap_username: "", imap_password: "", imap_folder: "INBOX", poll_interval_minutes: 5,
    smtp_host: "", smtp_port: 587, smtp_use_tls: true, smtp_username: "", smtp_password: "",
  });
  const [testing, setTesting] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(null);

  useEffect(() => {
    if (mb?.configured) {
      setForm((f) => ({
        ...f,
        email_address: mb.email_address || "",
        imap_host: mb.imap_host || "",
        imap_port: mb.imap_port || 993,
        imap_use_ssl: mb.imap_use_ssl !== false,
        imap_username: mb.imap_username || "",
        imap_password: "",
        imap_folder: mb.imap_folder || "INBOX",
        poll_interval_minutes: mb.poll_interval_minutes || 5,
        smtp_host: mb.smtp_host || "",
        smtp_port: mb.smtp_port || 587,
        smtp_use_tls: mb.smtp_use_tls !== false,
        smtp_username: mb.smtp_username || "",
        smtp_password: "",
      }));
    }
  }, [mb]);

  const testMut = useMutation({
    mutationFn: () => api.post("/integrations/imap/test", form).then((r) => r.data),
    onSuccess: (d) => { setTesting(d); if (d.ok) toast.success(`Connected — ${d.message_count ?? 0} msgs in ${d.folder}`); else toast.error(d.error || "Test failed"); },
    onError: (e) => setTesting({ ok: false, error: e?.response?.data?.detail || "Test failed" }),
  });
  const smtpTestMut = useMutation({
    mutationFn: () => api.post("/integrations/imap/smtp-test", {
      smtp_host: form.smtp_host, smtp_port: Number(form.smtp_port) || 587,
      smtp_use_tls: form.smtp_use_tls, smtp_username: form.smtp_username,
      smtp_password: form.smtp_password || undefined,
    }).then((r) => r.data),
    onSuccess: (d) => { setSmtpTesting(d); if (d.ok) toast.success("SMTP connected"); else toast.error(d.error || "SMTP test failed"); },
    onError: (e) => setSmtpTesting({ ok: false, error: e?.response?.data?.detail || "SMTP test failed" }),
  });
  const saveMut = useMutation({
    mutationFn: () => api.put("/integrations/imap/mailbox", form).then((r) => r.data),
    onSuccess: (d) => { qc.setQueryData(["my-mailbox"], d); toast.success("Mailbox saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const toggleMut = useMutation({
    mutationFn: () => api.post("/integrations/imap/toggle").then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["my-mailbox"] }); toast.success("Toggled"); },
  });
  const pollMut = useMutation({
    mutationFn: () => api.post("/integrations/imap/poll-now").then((r) => r.data),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["my-mailbox"] }); toast.success(`Poll finished — ${d.new_messages} new message(s)`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Poll failed"),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete("/integrations/imap/mailbox"),
    onSuccess: () => { qc.setQueryData(["my-mailbox"], { configured: false }); setForm({ email_address: "", imap_host: "", imap_port: 993, imap_use_ssl: true, imap_username: "", imap_password: "", imap_folder: "INBOX", poll_interval_minutes: 5 }); toast.success("Mailbox disconnected"); },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const presets = [
    { label: "Gmail", host: "imap.gmail.com", port: 993, ssl: true },
    { label: "Outlook / Microsoft 365", host: "outlook.office365.com", port: 993, ssl: true },
    { label: "iCloud", host: "imap.mail.me.com", port: 993, ssl: true },
    { label: "Yahoo", host: "imap.mail.yahoo.com", port: 993, ssl: true },
  ];

  return (
    <div className="max-w-3xl space-y-6" data-testid="mailbox-tab">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
              <Mail className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">My Mailbox</div>
              <div className="text-xs text-muted-foreground">Connect your work inbox. Incoming email lands in the CRM Inbox and auto-flags Leads.</div>
            </div>
          </div>
          {mb?.configured && (
            <Badge className={`rounded-sm ${mb.enabled ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"}`} data-testid="mailbox-status-badge">
              <Check className="h-3 w-3 mr-1" /> {mb.enabled ? "Connected · polling" : "Connected · paused"}
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {presets.map((p) => (
            <Button key={p.host} type="button" variant="outline" size="sm" className="rounded-sm text-xs"
              onClick={() => setForm({ ...form, imap_host: p.host, imap_port: p.port, imap_use_ssl: p.ssl })}
              data-testid={`imap-preset-${p.label.toLowerCase().replace(/[^a-z]/g,'-')}`}>
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Email address</Label>
            <Input data-testid="imap-email" className="mt-1.5 rounded-sm bg-secondary/40" placeholder="you@company.com" value={form.email_address} onChange={(e) => setForm({ ...form, email_address: e.target.value, imap_username: form.imap_username || e.target.value })} />
          </div>
          <div>
            <Label>Folder</Label>
            <Input data-testid="imap-folder" className="mt-1.5 rounded-sm bg-secondary/40" placeholder="INBOX" value={form.imap_folder} onChange={(e) => setForm({ ...form, imap_folder: e.target.value })} />
          </div>
          <div>
            <Label>IMAP host</Label>
            <Input data-testid="imap-host" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder="imap.gmail.com" value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} />
          </div>
          <div>
            <Label>Port</Label>
            <Input data-testid="imap-port" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Username</Label>
            <Input data-testid="imap-username" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" value={form.imap_username} onChange={(e) => setForm({ ...form, imap_username: e.target.value })} />
          </div>
          <div>
            <Label>Password {mb?.configured && <span className="text-muted-foreground text-xs ml-1">· leave blank to keep existing</span>}</Label>
            <Input data-testid="imap-password" type="password" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder={mb?.configured ? "•••• keeping current password ••••" : ""} value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} />
          </div>
          <div>
            <Label>Poll every</Label>
            <Select value={String(form.poll_interval_minutes)} onValueChange={(v) => setForm({ ...form, poll_interval_minutes: Number(v) })}>
              <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="imap-interval"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(choices?.choices || [2,5,10,15,30]).map((c) => (<SelectItem key={c} value={String(c)}>{c} minutes</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 mt-6">
            <Switch checked={form.imap_use_ssl} onCheckedChange={(v) => setForm({ ...form, imap_use_ssl: v })} data-testid="imap-ssl" />
            <span className="text-sm">Use SSL/TLS</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <Button variant="outline" className="rounded-sm" onClick={() => testMut.mutate()} disabled={testMut.isPending} data-testid="imap-test-btn">
            <PlugZap className="h-3.5 w-3.5 mr-1.5" />{testMut.isPending ? "Testing…" : "Test connection"}
          </Button>
          <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="imap-save-btn">
            {saveMut.isPending ? "Saving…" : (mb?.configured ? "Update" : "Connect mailbox")}
          </Button>
          {mb?.configured && (<>
            <Button variant="outline" className="rounded-sm" onClick={() => pollMut.mutate()} disabled={pollMut.isPending} data-testid="imap-poll-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Poll now</Button>
            <Button variant="outline" className="rounded-sm" onClick={() => toggleMut.mutate()} data-testid="imap-toggle-btn">{mb.enabled ? "Pause" : "Resume"}</Button>
            <Button variant="ghost" className="rounded-sm text-red-400 hover:bg-red-500/10" onClick={() => { if (window.confirm("Disconnect this mailbox?")) deleteMut.mutate(); }} data-testid="imap-delete-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Disconnect</Button>
          </>)}
        </div>

        {testing && (
          <div className={`mt-4 p-3 rounded-sm border text-xs ${testing.ok ? "border-green-500/30 bg-green-500/10 text-green-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`} data-testid="imap-test-result">
            {testing.ok ? (<><b>Connected</b> — {testing.message_count} message(s) in {testing.folder}</>) : (<><b>Error:</b> {testing.error}</>)}
          </div>
        )}

        {mb?.configured && mb.last_polled_at && (
          <div className="mt-4 text-xs text-muted-foreground" data-testid="imap-last-poll">
            Last polled: {new Date(mb.last_polled_at).toLocaleString()} · Last UID: {mb.last_uid || 0}
            {mb.last_error && <span className="text-red-400 ml-2">· Error: {mb.last_error}</span>}
          </div>
        )}
      </Card>

      {/* Outbound SMTP */}
      <Card className="rounded-sm bg-card border-border p-6" data-testid="smtp-card">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
              <Send className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">Outbound SMTP</div>
              <div className="text-xs text-muted-foreground">Replies to email conversations leave FROM your mailbox with proper threading headers.</div>
            </div>
          </div>
          {mb?.smtp_configured && (
            <Badge className="rounded-sm bg-green-500/10 text-green-400 border-green-500/30" data-testid="smtp-status-badge">
              <Check className="h-3 w-3 mr-1" /> Configured
            </Badge>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { label: "Gmail SMTP", host: "smtp.gmail.com", port: 587, tls: true },
            { label: "Outlook SMTP", host: "smtp.office365.com", port: 587, tls: true },
            { label: "iCloud SMTP", host: "smtp.mail.me.com", port: 587, tls: true },
          ].map((p) => (
            <Button key={p.host} type="button" variant="outline" size="sm" className="rounded-sm text-xs"
              onClick={() => setForm({ ...form, smtp_host: p.host, smtp_port: p.port, smtp_use_tls: p.tls, smtp_username: form.smtp_username || form.email_address })}
              data-testid={`smtp-preset-${p.label.split(" ")[0].toLowerCase()}`}>{p.label}</Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>SMTP host</Label>
            <Input data-testid="smtp-host" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder="smtp.gmail.com" value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} />
          </div>
          <div>
            <Label>Port</Label>
            <Input data-testid="smtp-port" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Username</Label>
            <Input data-testid="smtp-username" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" value={form.smtp_username} onChange={(e) => setForm({ ...form, smtp_username: e.target.value })} />
          </div>
          <div>
            <Label>Password {mb?.smtp_configured && <span className="text-muted-foreground text-xs ml-1">· leave blank to keep existing</span>}</Label>
            <Input data-testid="smtp-password" type="password" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder={mb?.smtp_configured ? "•••• keeping current ••••" : ""} value={form.smtp_password} onChange={(e) => setForm({ ...form, smtp_password: e.target.value })} />
          </div>
          <div className="flex items-center gap-3 mt-2 md:col-span-2">
            <Switch checked={form.smtp_use_tls} onCheckedChange={(v) => setForm({ ...form, smtp_use_tls: v })} data-testid="smtp-tls" />
            <span className="text-sm">Use STARTTLS (recommended for port 587). Port 465 uses SSL automatically.</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button variant="outline" className="rounded-sm" onClick={() => smtpTestMut.mutate()} disabled={smtpTestMut.isPending || !form.smtp_host || !form.smtp_username} data-testid="smtp-test-btn">
            <PlugZap className="h-3.5 w-3.5 mr-1.5" />{smtpTestMut.isPending ? "Testing…" : "Test SMTP"}
          </Button>
          <span className="text-xs text-muted-foreground">SMTP is saved together with your IMAP settings when you click <b>Save mailbox</b> above.</span>
        </div>
        {smtpTesting && (
          <div className={`mt-4 p-3 rounded-sm border text-xs ${smtpTesting.ok ? "border-green-500/30 bg-green-500/10 text-green-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`} data-testid="smtp-test-result">
            {smtpTesting.ok ? <b>SMTP connected — ready to send.</b> : (<><b>Error:</b> {smtpTesting.error}</>)}
          </div>
        )}
      </Card>

      <Card className="rounded-sm bg-secondary/30 border border-dashed border-border p-5">
        <div className="text-sm font-semibold flex items-center gap-2"><Info className="h-4 w-4 text-primary" />Getting an app password</div>
        <ul className="mt-2 text-xs text-muted-foreground space-y-1.5 list-disc ml-5">
          <li><b>Gmail</b>: enable 2FA, then create an <a className="text-primary hover:underline" target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords">App Password</a>.</li>
          <li><b>Outlook / M365</b>: enable 2FA, then create an app password at <a className="text-primary hover:underline" target="_blank" rel="noreferrer" href="https://account.microsoft.com/security">account.microsoft.com/security</a>.</li>
          <li><b>iCloud</b>: <a className="text-primary hover:underline" target="_blank" rel="noreferrer" href="https://appleid.apple.com/">appleid.apple.com</a> → Security → App-Specific Passwords.</li>
          <li>Passwords are encrypted at rest (Fernet). We only ever read from your inbox — never send.</li>
        </ul>
      </Card>
    </div>
  );
}


// ==============================================================
// Google Calendar — per-user OAuth
// ==============================================================
function GoogleCalendarTab() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({
    queryKey: ["google-status"],
    queryFn: () => api.get("/integrations/google/status").then((r) => r.data),
  });
  const { data: calendarsData } = useQuery({
    queryKey: ["google-calendars"],
    queryFn: () => api.get("/integrations/google/calendars").then((r) => r.data),
    enabled: !!status?.connected,
  });

  const connect = useMutation({
    mutationFn: () => api.get("/integrations/google/connect").then((r) => r.data),
    onSuccess: (d) => { window.location.href = d.authorization_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cannot start Google OAuth"),
  });
  const disconnect = useMutation({
    mutationFn: () => api.post("/integrations/google/disconnect").then((r) => r.data),
    onSuccess: () => { qc.setQueryData(["google-status"], { connected: false, configured: true }); toast.success("Disconnected"); },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="google-calendar-tab">
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
              <CalendarIcon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="font-display text-xl font-extrabold">Google Calendar</div>
              <div className="text-xs text-muted-foreground">When someone books via your scheduler, we'll create the event + a Google Meet link on your calendar.</div>
            </div>
          </div>
          {status?.connected && (
            <Badge className="rounded-sm bg-green-500/10 text-green-400 border-green-500/30" data-testid="google-connected-badge">
              <Check className="h-3 w-3 mr-1" /> Connected
            </Badge>
          )}
        </div>

        {!status?.configured && (
          <div className="p-3 rounded-sm border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 text-xs">
            Server admin hasn't configured Google OAuth credentials yet.
          </div>
        )}

        {status?.configured && !status.connected && (
          <div>
            <Button className="rounded-sm bg-primary" onClick={() => connect.mutate()} disabled={connect.isPending} data-testid="google-connect-btn">
              <PlugZap className="h-3.5 w-3.5 mr-1.5" />{connect.isPending ? "Redirecting…" : "Connect Google Calendar"}
            </Button>
            <div className="text-xs text-muted-foreground mt-2">
              You'll be redirected to Google to grant permission. We request read-only calendar list + write access to events (for creating meeting bookings).
            </div>
          </div>
        )}

        {status?.connected && (
          <div className="space-y-3">
            <div className="text-sm">
              Connected as <span className="font-mono text-primary" data-testid="google-email">{status.google_email}</span>
            </div>
            {(status.reauth_required || calendarsData?.reauth_required) && (
              <div className="p-3 rounded-sm border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 text-xs" data-testid="google-reauth-banner">
                Your Google authorisation has expired or was revoked. Reconnect to keep meetings syncing.
                <Button size="sm" className="rounded-sm ml-2 bg-primary" onClick={() => connect.mutate()} data-testid="google-reconnect-btn">Reconnect</Button>
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Connected {status.connected_at ? new Date(status.connected_at).toLocaleString() : ""}
              {!status.has_refresh && <span className="text-yellow-400 ml-2">· no refresh token (tokens will expire after 1 hour)</span>}
            </div>
            {calendarsData?.calendars?.length ? (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold mb-1.5">Your calendars ({calendarsData.calendars.length})</div>
                <div className="flex flex-wrap gap-1.5" data-testid="google-calendars-list">
                  {calendarsData.calendars.slice(0, 12).map((c) => (
                    <Badge key={c.id} className={`rounded-sm text-[10px] ${c.primary ? "bg-primary/20 text-primary" : "bg-secondary/60 text-muted-foreground"}`}>
                      {c.summary} {c.primary ? "· primary" : ""}
                    </Badge>
                  ))}
                </div>
              </div>
            ) : null}            <div>
              <Button variant="ghost" className="rounded-sm text-red-400 hover:bg-red-500/10" onClick={() => { if (window.confirm("Disconnect Google Calendar?")) disconnect.mutate(); }} data-testid="google-disconnect-btn">
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />Disconnect
              </Button>
            </div>
          </div>
        )}
      </Card>

      <Card className="rounded-sm bg-secondary/30 border border-dashed border-border p-5">
        <div className="text-sm font-semibold flex items-center gap-2"><Info className="h-4 w-4 text-primary" />What we do with your calendar</div>
        <ul className="mt-2 text-xs text-muted-foreground space-y-1.5 list-disc ml-5">
          <li>When a customer books a meeting through your public scheduler link, we create the event on your primary Google Calendar with attendees + description.</li>
          <li>A Google Meet video link is auto-generated and shared with everyone.</li>
          <li>We read your calendar list once (to show it here). We never read or modify events you didn't create through LeadifyCRM.</li>
          <li>Disconnect any time — no residual state.</li>
        </ul>
      </Card>
    </div>
  );
}



// ==============================================================
// Meta (Facebook Messenger + Instagram DMs) integration card
// Rendered inside IntegrationsTab (admin-only).
// ==============================================================
function MetaIntegrationCard({ copy }) {
  const qc = useQueryClient();
  const { data: info } = useQuery({
    queryKey: ["meta-info"],
    queryFn: () => api.get("/integrations/meta/info").then((r) => r.data),
  });
  const { data: pages = [] } = useQuery({
    queryKey: ["meta-pages"],
    queryFn: () => api.get("/integrations/meta/pages").then((r) => r.data),
  });
  const { data: fbPages, isFetching: fbLoading, refetch: refetchFbPages } = useQuery({
    queryKey: ["fb-pages"],
    queryFn: () => api.get("/integrations/facebook/pages").then((r) => r.data),
  });
  const [form, setForm] = useState({ page_id: "", page_access_token: "", channel_default: "facebook", instagram_account_id: "" });

  // Handle FB OAuth callback params
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("fb_connected") === "1") {
      toast.success("Facebook connected"); refetchFbPages();
      window.history.replaceState({}, "", window.location.pathname + "?tab=integrations");
    } else if (p.get("fb_error")) {
      toast.error(`Facebook connect failed: ${p.get("fb_error")}`);
      window.history.replaceState({}, "", window.location.pathname + "?tab=integrations");
    }
  }, []); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: (b) => api.put("/integrations/meta/pages", b).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["meta-pages"] }); toast.success("Page saved"); setForm({ page_id: "", page_access_token: "", channel_default: "facebook", instagram_account_id: "" }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/integrations/meta/pages/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["meta-pages"] }); qc.invalidateQueries({ queryKey: ["fb-pages"] }); toast.success("Page removed"); },
  });
  const connectMut = useMutation({
    mutationFn: () => api.get("/integrations/facebook/connect").then((r) => r.data),
    onSuccess: (d) => { window.location.href = d.authorization_url; },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cannot start Facebook OAuth"),
  });
  const disconnectFbMut = useMutation({
    mutationFn: () => api.post("/integrations/facebook/disconnect").then((r) => r.data),
    onSuccess: () => { qc.setQueryData(["fb-pages"], { connected: false, pages: [] }); toast.success("Disconnected"); },
  });
  const selectPageMut = useMutation({
    mutationFn: (page_id) => api.post("/integrations/facebook/select-page", { page_id }).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`${d.name} connected${d.webhook_subscribed ? " · webhook subscribed" : ""}`);
      qc.invalidateQueries({ queryKey: ["meta-pages"] });
      qc.invalidateQueries({ queryKey: ["fb-pages"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cannot save page"),
  });

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="meta-integration-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-blue-400" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold">Facebook Messenger + Instagram DMs</div>
            <div className="text-xs text-muted-foreground">One-click connect via Facebook Login, or wire it up manually below.</div>
          </div>
        </div>
        <Badge className="rounded-sm bg-blue-500/10 text-blue-400 border-blue-500/30" variant="outline">Advanced</Badge>
      </div>

      {/* ---- Facebook Login flow (uses pages_show_list) ---- */}
      <div className="rounded-sm border border-blue-500/30 bg-blue-500/5 p-4 mb-5" data-testid="fb-login-block">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><PlugZap className="h-4 w-4 text-blue-400" />Connect via Facebook Login</div>
          {fbPages?.connected && (
            <Badge className="rounded-sm bg-green-500/10 text-green-400 border-green-500/30" data-testid="fb-connected-badge">
              <Check className="h-3 w-3 mr-1" /> Signed in with Facebook
            </Badge>
          )}
        </div>
        {info && !info.fb_login_configured && (
          <div className="p-3 rounded-sm border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 text-xs" data-testid="fb-not-configured">
            <b>Server admin must set META_APP_ID and META_APP_SECRET</b> in <code>/app/backend/.env</code> to enable this button. The App ID + Secret come from your Meta Developer app → Settings → Basic.
          </div>
        )}
        {info?.fb_login_configured && !fbPages?.connected && (
          <div>
            <Button className="rounded-sm bg-primary" onClick={() => connectMut.mutate()} disabled={connectMut.isPending} data-testid="fb-connect-btn">
              <PlugZap className="h-3.5 w-3.5 mr-1.5" />{connectMut.isPending ? "Redirecting…" : "Connect Facebook Page"}
            </Button>
            <div className="text-xs text-muted-foreground mt-2">
              You'll be redirected to Facebook. We request <code>pages_show_list</code> + <code>pages_messaging</code> + <code>instagram_manage_messages</code>. After you approve, we'll show a picker of the Pages you manage — pick one to route into the CRM Inbox.
            </div>
          </div>
        )}
        {fbPages?.connected && (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-muted-foreground">Choose a Page from your account — we'll auto-subscribe its Messenger webhook.</div>
              <Button variant="ghost" size="sm" className="rounded-sm text-red-400" onClick={() => disconnectFbMut.mutate()} data-testid="fb-disconnect-btn"><LogOut className="h-3 w-3 mr-1" />Sign out</Button>
            </div>
            {fbLoading && <div className="text-xs text-muted-foreground py-2">Loading your Pages…</div>}
            <div className="space-y-1.5" data-testid="fb-pages-picker">
              {(fbPages.pages || []).map((p) => (
                <div key={p.id} className="flex items-center gap-2 p-2 rounded-sm bg-secondary/40" data-testid={`fb-page-${p.id}`}>
                  <div className="h-7 w-7 rounded-sm bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-black flex-none">{(p.name || "?").slice(0,2).toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {p.category} · Page ID: <span className="font-mono">{p.id}</span>
                      {p.instagram_username && <span> · IG @{p.instagram_username}</span>}
                    </div>
                  </div>
                  {p.already_saved ? (
                    <Badge className="rounded-sm text-[10px] bg-green-500/10 text-green-400 border-green-500/30" data-testid={`fb-page-saved-${p.id}`}><Check className="h-2.5 w-2.5 mr-0.5" />Connected</Badge>
                  ) : (
                    <Button size="sm" className="rounded-sm bg-primary h-7" onClick={() => selectPageMut.mutate(p.id)} disabled={selectPageMut.isPending} data-testid={`fb-page-select-${p.id}`}>
                      {selectPageMut.isPending ? "Saving…" : "Connect"}
                    </Button>
                  )}
                </div>
              ))}
              {fbPages.pages?.length === 0 && !fbLoading && (
                <div className="text-xs text-yellow-400 p-2" data-testid="fb-no-pages">
                  This Facebook account doesn't manage any Pages yet. Create one at <a className="underline" target="_blank" rel="noreferrer" href="https://www.facebook.com/pages/create">facebook.com/pages/create</a> and refresh.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---- Manual webhook config (kept for advanced users) ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <IntegrationsCopyField label="Callback URL" value={info?.callback_url || ""} testId="meta-callback" onCopy={copy} />
        <IntegrationsCopyField label="Verify Token" value={info?.verify_token || ""} testId="meta-verify" onCopy={copy} />
      </div>
      <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
        <ExternalLink className="h-3 w-3" />
        In Meta Developer → Webhooks → paste the URL + verify token, subscribe to <code>messages</code> and <code>messaging_postbacks</code>.
      </div>
      {info && !info.app_secret_configured && (
        <div className="mt-3 p-2 rounded-sm border border-yellow-500/30 bg-yellow-500/10 text-yellow-300 text-xs">
          <b>Warning:</b> META_APP_SECRET not set — inbound signatures are accepted without verification. Set it in production.
        </div>
      )}

      <div className="border-t border-border/60 mt-6 pt-5">
        <div className="text-sm font-semibold mb-3">Connected Pages ({pages.length})</div>
        {pages.length === 0 && <div className="text-xs text-muted-foreground mb-3">No pages configured yet. When Meta first calls the webhook, the page will auto-provision to the first admin's division — but you can pre-map it here to a specific salesperson.</div>}
        <div className="space-y-2" data-testid="meta-pages-list">
          {pages.map((p) => (
            <div key={p.page_id} className="flex items-center gap-2 p-2 rounded-sm bg-secondary/40" data-testid={`meta-page-${p.page_id}`}>
              <Badge className="rounded-sm text-[10px] bg-blue-500/15 text-blue-400">{p.channel_default}</Badge>
              <div className="text-xs font-mono flex-1 truncate">{p.page_name || p.page_id}</div>
              <div className="text-[10px] text-muted-foreground">{p.has_token ? "token set" : "using default"}</div>
              {p.instagram_account_id && <Badge className="rounded-sm text-[10px] bg-pink-500/15 text-pink-400">IG: {p.instagram_account_id.slice(0, 10)}…</Badge>}
              <Button variant="ghost" size="sm" className="rounded-sm text-red-400 hover:bg-red-500/10 h-7" onClick={() => { if (window.confirm(`Remove page ${p.page_id}?`)) deleteMut.mutate(p.page_id); }} data-testid={`meta-page-delete-${p.page_id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>

        <details className="mt-4">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-white" data-testid="meta-manual-toggle">Advanced: add a Page manually (paste Page ID + Access Token)</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div>
              <Label>Facebook Page ID</Label>
              <Input data-testid="meta-form-page-id" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder="1234567890" value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} />
            </div>
            <div>
              <Label>Instagram Business Account ID <span className="text-muted-foreground text-xs ml-1">(optional)</span></Label>
              <Input data-testid="meta-form-ig-id" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder="17841000000000000" value={form.instagram_account_id} onChange={(e) => setForm({ ...form, instagram_account_id: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Page Access Token <span className="text-muted-foreground text-xs ml-1">(leave blank to use the server default)</span></Label>
              <Input data-testid="meta-form-token" type="password" className="mt-1.5 rounded-sm bg-secondary/40 font-mono" placeholder="EAAOja..." value={form.page_access_token} onChange={(e) => setForm({ ...form, page_access_token: e.target.value })} />
            </div>
          </div>
          <div className="mt-3">
            <Button className="rounded-sm bg-primary" onClick={() => saveMut.mutate({ ...form, page_id: form.page_id.trim() })} disabled={!form.page_id.trim() || saveMut.isPending} data-testid="meta-form-save">
              <Plus className="h-3.5 w-3.5 mr-1.5" />{saveMut.isPending ? "Saving…" : "Add / update page"}
            </Button>
          </div>
        </details>
      </div>
    </Card>
  );
}


// ==============================================================
// Division Timezone + Task-alerts scheduler card
// ==============================================================
const TIMEZONES = [
  "UTC", "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Moscow",
  "Africa/Cairo", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Dhaka",
  "Asia/Bangkok", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Shanghai", "Asia/Tokyo",
  "Australia/Sydney", "Pacific/Auckland",
];

function DivisionScheduleCard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: divisions = [] } = useQuery({
    queryKey: ["divisions-for-schedule"],
    queryFn: () => api.get("/divisions").then((r) => r.data),
  });
  const [selected, setSelected] = useState("");
  useEffect(() => {
    if (!selected && divisions.length) setSelected(user?.division_id || divisions[0].id);
  }, [divisions, selected, user]);

  const { data: schedule } = useQuery({
    queryKey: ["division-schedule", selected],
    queryFn: () => api.get(`/divisions/${selected}/schedule`).then((r) => r.data),
    enabled: !!selected,
  });

  const saveMut = useMutation({
    mutationFn: (b) => api.put(`/divisions/${selected}/schedule`, b).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["division-schedule", selected] }); toast.success("Schedule saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const testMut = useMutation({
    mutationFn: () => api.post("/task-alerts/send-all").then((r) => r.data),
    onSuccess: (d) => toast.success(`Sent ${d.sent} digest(s), skipped ${d.skipped}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="division-schedule-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
            <CalendarIcon className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold">Daily task alerts</div>
            <div className="text-xs text-muted-foreground">Automatic morning digest at 09:00 division-local. Everyone with overdue/due-today tasks gets one email.</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Division</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="schedule-division"><SelectValue /></SelectTrigger>
            <SelectContent>
              {divisions.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Timezone</Label>
          <Select value={schedule?.timezone || "UTC"} onValueChange={(v) => saveMut.mutate({ timezone: v, task_alerts_enabled: schedule?.task_alerts_enabled })}>
            <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="schedule-timezone"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 overflow-auto">
              {TIMEZONES.map((tz) => (<SelectItem key={tz} value={tz}>{tz}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Switch checked={schedule?.task_alerts_enabled !== false} onCheckedChange={(v) => saveMut.mutate({ timezone: schedule?.timezone || "UTC", task_alerts_enabled: v })} data-testid="schedule-enable" />
          <div className="text-sm mb-2">Daily alerts enabled</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-muted-foreground">
        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => testMut.mutate()} disabled={testMut.isPending} data-testid="schedule-send-now">
          <Send className="h-3.5 w-3.5 mr-1.5" />{testMut.isPending ? "Sending…" : "Send test digest to team now"}
        </Button>
        {schedule?.task_alerts_last_sent_at && (
          <span data-testid="schedule-last-sent">Last auto-sent: {new Date(schedule.task_alerts_last_sent_at).toLocaleString()}</span>
        )}
      </div>
    </Card>
  );
}


// ==============================================================
// Monthly Statement of Accounts auto-send (iter 30)
// ==============================================================
function MonthlyStatementsCard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: divisions = [] } = useQuery({
    queryKey: ["divisions-for-schedule"],
    queryFn: () => api.get("/divisions").then((r) => r.data),
  });
  const [selected, setSelected] = useState("");
  useEffect(() => {
    if (!selected && divisions.length) setSelected(user?.division_id || divisions[0].id);
  }, [divisions, selected, user]);

  const { data: schedule } = useQuery({
    queryKey: ["division-schedule", selected],
    queryFn: () => api.get(`/divisions/${selected}/schedule`).then((r) => r.data),
    enabled: !!selected,
  });

  const { data: preview } = useQuery({
    queryKey: ["monthly-statement-preview"],
    queryFn: () => api.get("/statements/monthly-preview").then((r) => r.data),
  });

  const saveMut = useMutation({
    mutationFn: (b) => api.put(`/divisions/${selected}/schedule`, {
      timezone: schedule?.timezone || "UTC", ...b,
    }).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["division-schedule", selected] }); toast.success("Saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const runMut = useMutation({
    mutationFn: () => api.post("/statements/run-monthly-now").then((r) => r.data),
    onSuccess: (d) => toast.success(`Sent ${d.sent}, skipped ${d.skipped}, ${d.failures} failed`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Run failed"),
  });

  const outstanding = preview?.candidates || [];
  const outstandingTotal = outstanding.reduce((s, c) => s + (c.balance || 0), 0);
  const currency = "SGD";

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="monthly-statements-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
            <Mail className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold">Monthly Statement auto-send</div>
            <div className="text-xs text-muted-foreground">On the configured day &amp; hour of every month, LeadifyCRM emails a fresh Statement of Accounts to every contact with an outstanding balance.</div>
          </div>
        </div>
      </div>

      {/* Preview — who would receive it right now */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-sm border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Contacts owing</div>
          <div className="font-display text-2xl font-black mt-1" data-testid="ms-preview-count">{outstanding.length}</div>
        </div>
        <div className="rounded-sm border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Outstanding total</div>
          <div className="font-display text-2xl font-black mt-1 text-amber-400" data-testid="ms-preview-total">
            {new Intl.NumberFormat(undefined, { style: "currency", currency }).format(outstandingTotal)}
          </div>
        </div>
        <div className="rounded-sm border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Sender mailbox</div>
          <div className={`text-sm font-semibold mt-1 truncate ${preview?.sender ? "text-foreground" : "text-red-400"}`} data-testid="ms-preview-sender">
            {preview?.sender ? preview.sender.email : "No SMTP configured — set up a mailbox first"}
          </div>
        </div>
      </div>

      {/* Config row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <Label>Division</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="ms-division"><SelectValue /></SelectTrigger>
            <SelectContent>
              {divisions.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Day of month</Label>
          <Select
            value={String(schedule?.monthly_statements_day || 1)}
            onValueChange={(v) => saveMut.mutate({ monthly_statements_day: parseInt(v, 10) })}
          >
            <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="ms-day"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 overflow-auto">
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <SelectItem key={d} value={String(d)}>Day {d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Hour (local time)</Label>
          <Select
            value={String(schedule?.monthly_statements_hour || 9)}
            onValueChange={(v) => saveMut.mutate({ monthly_statements_hour: parseInt(v, 10) })}
          >
            <SelectTrigger className="mt-1.5 rounded-sm bg-secondary/40" data-testid="ms-hour"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72 overflow-auto">
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end gap-2">
          <Switch
            checked={!!schedule?.monthly_statements_enabled}
            onCheckedChange={(v) => saveMut.mutate({ monthly_statements_enabled: v })}
            data-testid="ms-enable"
          />
          <div className="text-sm mb-2">Auto-send enabled</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-muted-foreground">
        <Button
          variant="outline" size="sm" className="rounded-sm"
          onClick={() => runMut.mutate()}
          disabled={runMut.isPending}
          data-testid="ms-run-now"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />{runMut.isPending ? "Sending…" : "Run for all customers now"}
        </Button>
        {schedule?.monthly_statements_last_sent_at && (
          <span data-testid="ms-last-sent">Last auto-sent: {new Date(schedule.monthly_statements_last_sent_at).toLocaleString()}</span>
        )}
        {schedule?.timezone && (
          <span>Time zone: <span className="text-foreground font-semibold">{schedule.timezone}</span></span>
        )}
      </div>

      {outstanding.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-primary hover:underline" data-testid="ms-preview-toggle">
            Preview: which {outstanding.length} contact{outstanding.length === 1 ? "" : "s"} will be emailed?
          </summary>
          <div className="mt-2 border border-border rounded-sm max-h-64 overflow-y-auto">
            <div className="grid grid-cols-[2fr_2fr_80px_120px] gap-2 px-3 py-2 border-b border-border bg-secondary/30 text-[10px] uppercase tracking-widest text-muted-foreground font-bold sticky top-0">
              <div>Contact</div><div>Email</div><div className="text-right">Invoices</div><div className="text-right">Balance</div>
            </div>
            {outstanding.map((c) => (
              <div key={c.contact_id} className="grid grid-cols-[2fr_2fr_80px_120px] gap-2 px-3 py-2 border-b border-border last:border-b-0 text-xs items-center">
                <div className="truncate" title={c.name}>{c.name}</div>
                <div className="truncate text-muted-foreground" title={c.email}>{c.email || "—"}</div>
                <div className="text-right">{c.invoice_count}</div>
                <div className="text-right font-semibold text-amber-400">{new Intl.NumberFormat(undefined, { style: "currency", currency }).format(c.balance)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}


// ==============================================================
// Per-division WhatsWizard API credentials
// ==============================================================
function WhatsExtCredentialsCard({ copy }) {
  const qc = useQueryClient();
  const { data: creds = [] } = useQuery({
    queryKey: ["wa-creds"],
    queryFn: () => api.get("/integrations/whatsapp-ext/credentials").then((r) => r.data),
  });
  const [name, setName] = useState("");
  const [reveal, setReveal] = useState(null);

  const createMut = useMutation({
    mutationFn: () => api.post("/integrations/whatsapp-ext/credentials", { name }).then((r) => r.data),
    onSuccess: (d) => { setReveal(d.reveal_once); setName(""); qc.invalidateQueries({ queryKey: ["wa-creds"] }); toast.success("Credential created"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Create failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/integrations/whatsapp-ext/credentials/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["wa-creds"] }); toast.success("Removed"); },
  });

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="wa-creds-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-green-500/15 border border-green-500/30 flex items-center justify-center">
            <MessageCircle className="h-4 w-4 text-green-400" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold">WhatsWizard credentials (per-division)</div>
            <div className="text-xs text-muted-foreground">Give each division its own API key so their Chrome extension only routes messages to their own inbox.</div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-4" data-testid="wa-creds-list">
        {creds.length === 0 && <div className="text-xs text-muted-foreground">No credentials yet — create one below.</div>}
        {creds.map((c) => (
          <div key={c.id} className="flex items-center gap-2 p-2 rounded-sm bg-secondary/40" data-testid={`wa-cred-${c.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{c.name}</div>
              <div className="text-[10px] text-muted-foreground font-mono truncate">{c.api_key_masked} · {c.last_used_at ? `last used ${new Date(c.last_used_at).toLocaleString()}` : "never used"}</div>
            </div>
            <Button variant="ghost" size="sm" className="rounded-sm text-red-400 hover:bg-red-500/10 h-7" onClick={() => { if (window.confirm(`Delete "${c.name}"? Any extension using its key will stop working.`)) deleteMut.mutate(c.id); }} data-testid={`wa-cred-delete-${c.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
        <Input data-testid="wa-cred-name" className="rounded-sm bg-secondary/40" placeholder="e.g. Sales · John's laptop" value={name} onChange={(e) => setName(e.target.value)} />
        <Button className="rounded-sm bg-primary" onClick={() => createMut.mutate()} disabled={!name.trim() || createMut.isPending} data-testid="wa-cred-create">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{createMut.isPending ? "Creating…" : "Generate key"}
        </Button>
      </div>

      {reveal && (
        <div className="mt-4 p-3 rounded-sm border border-green-500/30 bg-green-500/10" data-testid="wa-cred-reveal">
          <div className="text-xs font-semibold text-green-300 mb-1.5">Copy this key now — we don't show it again.</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-black/40 p-2 rounded-sm font-mono break-all" data-testid="wa-cred-reveal-value">{reveal}</code>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => { try { copy(reveal); } catch (e) { toast.error("Copy failed — please select and copy manually"); } }} data-testid="wa-cred-reveal-copy"><Copy className="h-3 w-3" /></Button>
            <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" onClick={() => setReveal(null)}>Dismiss</Button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">Paste into the WhatsWizard Chrome extension → Settings → API key. The extension now routes messages ONLY to this division.</div>
        </div>
      )}
    </Card>
  );
}


// ==============================================================
// Task Statuses (per-division customisable)
// ==============================================================
function TaskStatusesCard() {
  const qc = useQueryClient();
  const { data: statuses = [] } = useQuery({
    queryKey: ["task-statuses"],
    queryFn: () => api.get("/task-statuses").then((r) => r.data),
  });
  const [form, setForm] = useState({ key: "", label: "", color: "#94A3B8", order: 999, is_terminal: false });

  const createMut = useMutation({
    mutationFn: () => api.post("/task-statuses", form).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["task-statuses"] }); setForm({ key: "", label: "", color: "#94A3B8", order: 999, is_terminal: false }); toast.success("Status added"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/task-statuses/${id}`, patch).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["task-statuses"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/task-statuses/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["task-statuses"] }); toast.success("Status removed — existing tasks reassigned to To do"); },
  });

  return (
    <Card className="rounded-sm bg-card border-border p-6" data-testid="task-statuses-card">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-primary/20 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-display text-xl font-extrabold">Task statuses</div>
            <div className="text-xs text-muted-foreground">Customise the workflow states available on Project tasks. Each division has its own list.</div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-5" data-testid="task-statuses-list">
        {statuses.map((s) => (
          <div key={s.id} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] items-center gap-2 p-2 rounded-sm bg-secondary/40" data-testid={`task-status-${s.key}`}>
            <input type="color" value={s.color} onChange={(e) => updateMut.mutate({ id: s.id, patch: { color: e.target.value } })} className="w-8 h-8 rounded-sm border-0 bg-transparent cursor-pointer" data-testid={`task-status-color-${s.key}`} />
            <div>
              <Input value={s.label} onChange={(e) => updateMut.mutate({ id: s.id, patch: { label: e.target.value } })} className="rounded-sm bg-transparent border-0 h-7 p-0 font-semibold" data-testid={`task-status-label-${s.key}`} />
              <div className="text-[10px] text-muted-foreground font-mono">{s.key}</div>
            </div>
            <Input type="number" value={s.order} onChange={(e) => updateMut.mutate({ id: s.id, patch: { order: Number(e.target.value) } })} className="rounded-sm bg-secondary/40 h-7 w-16 text-xs" data-testid={`task-status-order-${s.key}`} />
            <div className="flex items-center gap-1">
              <Switch checked={s.is_terminal} onCheckedChange={(v) => updateMut.mutate({ id: s.id, patch: { is_terminal: v } })} data-testid={`task-status-terminal-${s.key}`} />
              <span className="text-[10px] text-muted-foreground">done-like</span>
            </div>
            <Badge className="rounded-sm text-[10px]" style={{ backgroundColor: s.color + "22", color: s.color, borderColor: s.color + "55" }}>{s.label}</Badge>
            <Button variant="ghost" size="sm" className="rounded-sm text-red-400 hover:bg-red-500/10 h-7" onClick={() => { if (window.confirm(`Delete "${s.label}"? Any tasks using this status will fall back to "To do".`)) deleteMut.mutate(s.id); }} data-testid={`task-status-delete-${s.key}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      <div className="border-t border-border/60 pt-4">
        <div className="text-xs font-semibold mb-2">Add a status</div>
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto_auto] gap-2 items-center">
          <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-9 h-9 rounded-sm border-0 bg-transparent cursor-pointer" data-testid="task-status-new-color" />
          <Input placeholder="key (e.g. under_review)" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} className="rounded-sm bg-secondary/40" data-testid="task-status-new-key" />
          <Input placeholder="Label (e.g. Under Review)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-sm bg-secondary/40" data-testid="task-status-new-label" />
          <div className="flex items-center gap-1">
            <Switch checked={form.is_terminal} onCheckedChange={(v) => setForm({ ...form, is_terminal: v })} data-testid="task-status-new-terminal" />
            <span className="text-[10px] text-muted-foreground">done-like</span>
          </div>
          <Button className="rounded-sm bg-primary" onClick={() => createMut.mutate()} disabled={!form.key.trim() || !form.label.trim() || createMut.isPending} data-testid="task-status-new-save">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add
          </Button>
        </div>
      </div>
    </Card>
  );
}

function BillingProfileTab() {
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "company_admin";
  const [targetDivisionId, setTargetDivisionId] = useState(user?.division_id || null);
  const { data: divisions = [] } = useQuery({
    queryKey: ["divisions-for-billing"],
    queryFn: () => api.get("/divisions").then((r) => r.data),
    enabled: isCompanyAdmin,
  });
  useEffect(() => {
    if (!targetDivisionId && user?.division_id) setTargetDivisionId(user.division_id);
  }, [user, targetDivisionId]);

  const { data: profile, isLoading } = useQuery({
    queryKey: ["billing-profile", targetDivisionId],
    queryFn: () => api.get("/billing-profile", {
      params: targetDivisionId && targetDivisionId !== user?.division_id ? { division_id: targetDivisionId } : {},
    }).then((r) => r.data),
    enabled: !!targetDivisionId,
  });
  const [state, setState] = useState(null);
  const qc = useQueryClient();
  // Reset local edit state whenever the selected division changes so we don't
  // accidentally save edits from division A into division B.
  useEffect(() => { setState(null); }, [targetDivisionId]);
  useEffect(() => { if (profile && !state) setState(profile); }, [profile, state]);

  const save = useMutation({
    mutationFn: () => api.put("/billing-profile", state, {
      params: targetDivisionId && targetDivisionId !== user?.division_id ? { division_id: targetDivisionId } : {},
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["billing-profile", targetDivisionId] }); toast.success("Billing profile saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const onQrChange = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Please upload a QR image smaller than 500KB (crop or compress it).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setState((s) => ({ ...s, paynow_qr_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const setField = (k, v) => setState((s) => ({ ...s, [k]: v }));

  if (isLoading || !state) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-6xl" data-testid="billing-profile-tab">
      {isCompanyAdmin && divisions.length > 1 && (
        <Card className="rounded-sm bg-card border-border p-4 mb-4 flex items-center gap-4 flex-wrap" data-testid="bp-division-switcher">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">Editing division</div>
              <div className="text-xs text-muted-foreground">Each division has its own logo, accent colour, invoice header &amp; bank details.</div>
            </div>
          </div>
          <Select value={targetDivisionId || ""} onValueChange={(v) => setTargetDivisionId(v)}>
            <SelectTrigger className="rounded-sm bg-secondary/40 w-56" data-testid="bp-division-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {divisions.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.name}{d.id === user.division_id ? " (yours)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {targetDivisionId && targetDivisionId !== user.division_id && (
            <span className="text-[10px] uppercase tracking-widest text-amber-500 font-bold">Editing another division</span>
          )}
        </Card>
      )}
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div className="space-y-6">
        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Company details</div>
          <div className="font-display text-xl font-extrabold mb-4">This shows on every invoice</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Company name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.company_name || ""} onChange={(e) => setField("company_name", e.target.value)} data-testid="bp-company-name" /></div>
            <div><Label>UEN</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.uen || ""} onChange={(e) => setField("uen", e.target.value)} data-testid="bp-uen" /></div>
            <div><Label>Postal code</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.postal_code || ""} onChange={(e) => setField("postal_code", e.target.value)} data-testid="bp-postal" /></div>
            <div className="col-span-2"><Label>Address</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.address || ""} onChange={(e) => setField("address", e.target.value)} data-testid="bp-address" /></div>
            <div><Label>Email</Label><Input type="email" className="rounded-sm mt-1.5 bg-secondary/40" value={state.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="bp-email" /></div>
            <div><Label>Phone</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.phone || ""} onChange={(e) => setField("phone", e.target.value)} data-testid="bp-phone" /></div>
            <div className="col-span-2"><Label>Website (optional)</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.website || ""} onChange={(e) => setField("website", e.target.value)} placeholder="www.example.com" /></div>
            <div className="col-span-2"><Label>Tagline (optional, prints at the bottom of the invoice)</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.default_tagline || ""} onChange={(e) => setField("default_tagline", e.target.value)} placeholder="Weddings are made in Heaven, Shaabaz makes it Happen" /></div>
          </div>
        </Card>

        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Payment details</div>
          <div className="font-display text-xl font-extrabold mb-4">Bank transfer &amp; PayNow</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Bank name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.bank_name || ""} onChange={(e) => setField("bank_name", e.target.value)} placeholder="OCBC Current Account" data-testid="bp-bank-name" /></div>
            <div><Label>Bank account no.</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.bank_account_no || ""} onChange={(e) => setField("bank_account_no", e.target.value)} placeholder="629-092743-001" data-testid="bp-bank-no" /></div>
            <div className="col-span-2"><Label>Account holder name</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.bank_account_name || ""} onChange={(e) => setField("bank_account_name", e.target.value)} data-testid="bp-bank-name-holder" /></div>
            <div><Label>PayNow UEN</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.paynow_uen || ""} onChange={(e) => setField("paynow_uen", e.target.value)} data-testid="bp-paynow-uen" /></div>
            <div><Label>Cheque payable to</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={state.cheque_payable_to || ""} onChange={(e) => setField("cheque_payable_to", e.target.value)} data-testid="bp-cheque" /></div>
          </div>
        </Card>

        <Card className="rounded-sm bg-card border-border p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-3">Default terms &amp; notes</div>
          <div className="font-display text-xl font-extrabold mb-4">Pre-fills new invoices</div>
          <div className="space-y-3">
            <div><Label>Default terms (one clause per line)</Label>
              <Textarea rows={8} className="rounded-sm mt-1.5 bg-secondary/40" value={(state.default_terms || []).join("\n")} onChange={(e) => setField("default_terms", e.target.value.split("\n"))} data-testid="bp-terms" />
            </div>
            <div><Label>Default notes</Label>
              <Textarea rows={3} className="rounded-sm mt-1.5 bg-secondary/40" value={state.default_notes || ""} onChange={(e) => setField("default_notes", e.target.value)} data-testid="bp-notes" />
            </div>
            <div>
              <Label>Default service fee <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— % of subtotal applied to opted-in line items</span></Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  className="rounded-sm bg-secondary/40 w-28"
                  value={state.default_service_fee_pct ?? 10}
                  onChange={(e) => setField("default_service_fee_pct", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                  data-testid="bp-service-fee-pct"
                />
                <span className="text-sm text-muted-foreground">%</span>
                <span className="text-xs text-muted-foreground ml-2">Each new quote/invoice starts with this. Individual documents can override.</span>
              </div>
            </div>
          </div>
        </Card>

        <Button className="rounded-sm bg-primary" onClick={() => save.mutate()} disabled={save.isPending} data-testid="bp-save">Save billing profile</Button>
      </div>

      <div className="space-y-4">
        <Card className="rounded-sm bg-card border-border p-6" data-testid="bp-branding-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">Statement &amp; Receipt Branding</div>
          <div className="font-display text-xl font-extrabold mb-4">Logo &amp; accent colour</div>

          <Label>Company logo</Label>
          {state.logo_data_uri ? (
            <img
              src={state.logo_data_uri}
              alt="Company logo"
              className="w-full h-32 object-contain rounded-sm border border-border mt-1.5 mb-3 bg-white p-3"
              data-testid="bp-logo-preview"
            />
          ) : (
            <div className="w-full h-32 rounded-sm border-2 border-dashed border-border flex items-center justify-center text-xs text-muted-foreground mt-1.5 mb-3">No logo uploaded</div>
          )}
          <label className="block">
            <div className="rounded-sm bg-primary/10 border border-primary/40 text-primary text-center py-2 text-sm font-semibold cursor-pointer hover:bg-primary/20 transition-colors">Upload logo</div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              data-testid="bp-logo-upload"
              onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 500 * 1024) { toast.error("Please upload a logo under 500KB (crop or compress it)."); return; }
                const reader = new FileReader();
                reader.onload = () => setState((s) => ({ ...s, logo_data_uri: reader.result }));
                reader.readAsDataURL(file);
              }}
            />
          </label>
          {state.logo_data_uri && (
            <button
              onClick={() => setField("logo_data_uri", null)}
              className="text-xs text-muted-foreground hover:text-red-400 mt-2 w-full text-center"
              data-testid="bp-logo-remove"
            >Remove logo</button>
          )}
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">Recommended: 512×512 PNG on transparent background</div>

          <div className="mt-6">
            <Label>Accent colour</Label>
            <div className="flex items-center gap-2 mt-1.5">
              <input
                type="color"
                value={state.accent_color || "#7C3AED"}
                onChange={(e) => setField("accent_color", e.target.value)}
                className="h-9 w-14 rounded-sm border border-border bg-secondary/40 cursor-pointer"
                data-testid="bp-accent-color-picker"
              />
              <Input
                className="rounded-sm bg-secondary/40 flex-1"
                value={state.accent_color || "#7C3AED"}
                onChange={(e) => setField("accent_color", e.target.value)}
                placeholder="#7C3AED"
                data-testid="bp-accent-color-hex"
              />
            </div>
            <div className="mt-3 flex items-center gap-2">
              {["#7C3AED", "#0EA5E9", "#EF4444", "#10B981", "#F59E0B", "#0F172A"].map((c) => (
                <button
                  key={c}
                  onClick={() => setField("accent_color", c)}
                  className="h-6 w-6 rounded-sm border-2 border-transparent hover:border-foreground/40 transition-colors"
                  style={{ background: c, borderColor: state.accent_color === c ? "#fff" : "transparent" }}
                  title={c}
                  data-testid={`bp-accent-swatch-${c.replace("#", "")}`}
                />
              ))}
            </div>
            <div className="mt-4 rounded-sm p-3" style={{ background: `${state.accent_color || "#7C3AED"}12`, border: `1.5px solid ${state.accent_color || "#7C3AED"}` }}>
              <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: state.accent_color || "#7C3AED" }}>Preview</div>
              <div className="text-sm font-bold mt-1">This is how your receipt hero will look</div>
            </div>
          </div>
        </Card>

        <Card className="rounded-sm bg-card border-border p-6" data-testid="bp-qr-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold mb-2">PayNow QR</div>
          <div className="font-display text-xl font-extrabold mb-4">Upload your QR</div>
          {state.paynow_qr_image ? (
            <img src={state.paynow_qr_image} alt="PayNow QR" className="w-full aspect-square object-contain rounded-sm border border-border mb-3 bg-white p-3" data-testid="bp-qr-preview" />
          ) : (
            <div className="w-full aspect-square rounded-sm border-2 border-dashed border-border flex items-center justify-center text-xs text-muted-foreground mb-3">No QR uploaded</div>
          )}
          <label className="block">
            <div className="rounded-sm bg-primary/10 border border-primary/40 text-primary text-center py-2 text-sm font-semibold cursor-pointer hover:bg-primary/20 transition-colors">Choose image</div>
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onQrChange(e.target.files?.[0])} data-testid="bp-qr-upload" />
          </label>
          {state.paynow_qr_image && (
            <button onClick={() => setField("paynow_qr_image", null)} className="text-xs text-muted-foreground hover:text-red-400 mt-2 w-full text-center" data-testid="bp-qr-remove">Remove QR</button>
          )}
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-3">Recommended: 512×512 PNG, under 500KB</div>
        </Card>
      </div>
      </div>
    </div>
  );
}

